import React, { FC, ReactElement, useMemo } from 'react';
import styled, { css, ThemeProvider } from 'styled-components';
import { colors, shadows } from 'src/config/colors';
import { typography } from 'src/config/typography';
import ClockIcon from 'src/assets/icons/ClockIcon';
import { format } from 'src/lib/date';
import { media } from 'src/config/breakpoints';
import { layers } from 'src/config/layers';
import { addMinutes } from 'date-fns';
import { CourseEvent } from 'src/common/models/event';
import { CourseEventStatusLabel } from 'src/public/components/course-page/CourseEventSelectPanel/CourseEventStatusLabel';
import { CleanButton } from 'src/common/components/Button';

const Item = styled.div<{ $link?: boolean }>`
  width: 100%;
  display: grid;
  font-size: 0.75rem;
  gap: 0.25rem 0.75rem;
  grid-template-areas: 'date time status' 'date actions actions';
  align-items: center;
  grid-template-columns: 3.625rem 1fr 1fr;
  line-height: 1rem;
  opacity: ${({ theme }) => (!theme.active && theme.dimmed ? 0.5 : 1)};
  padding: 0.75rem 0 0;
  position: relative;

  ${media.w.min.px768(css`
    gap: 0.4375rem 0.75rem;
    grid-template-areas:
      'date time'
      'date status'
      'actions actions';
    grid-template-columns: 3.75rem 1fr;
  `)};

  ${media.w.min.px1200(css`
    grid-template-columns: 3.75rem 1fr;
  `)};

  ${({ $link }) =>
    $link &&
    css`
      gap: 0 1rem;
      grid-template-areas: 'date time status';
      padding: 0.5rem 0.5625rem;
      cursor: pointer;

      &:hover {
        box-shadow: ${shadows.redHover};
        z-index: ${layers.contentBottom};
      }

      ${media.w.min.px768(css`
        padding: 0.625rem;
        grid-template-areas:
          'date time'
          'date status';
      `)};
    `};

  ${({ theme }) =>
    theme.active
      ? css`
          background: ${colors.blue};
          box-shadow: ${shadows.drop3};
          border-radius: 0.25rem;
          color: ${colors.white};
        `
      : css`
          color: ${colors.blue};

          &:nth-child(even) {
            background: ${colors.grey08};
          }
        `};
`;

const Date = styled.div<{ $link?: boolean }>`
  align-items: center;
  background: ${colors.white};
  border: 0.0625rem solid ${colors.darkGrey};
  border-radius: 0.25rem;
  color: ${colors.blue};
  display: flex;
  font-size: 0.875rem;
  font-weight: 300;
  grid-area: date;
  justify-content: center;
  line-height: 1rem;
  text-transform: uppercase;
  height: 3.625rem;
  width: 3.625rem;
  flex-direction: column;

  ${({ $link }) =>
    $link &&
    css`
      flex-direction: row;
      font-size: 0.75rem;
      height: 1.625rem;
    `};

  ${media.w.min.px768(css`
    flex-direction: column;
    font-size: 0.875rem;
    height: 3.75rem;
    line-height: 1.25rem;
    width: 3.75rem;
  `)};

  span {
    color: ${colors.pfp3};
    font-family: ${typography.fontFamilyHeaders};
    font-size: 1.5rem;
    font-weight: 400;
    line-height: 1.5625rem;

    ${({ $link }) =>
      $link &&
      css`
        margin-right: 0.1875rem;
        font-size: 1.25rem;
      `};

    ${media.w.min.px768(css`
      font-size: 1.5rem;
      line-height: 1.875rem;
      margin-right: 0;
    `)};
  }
`;

const StyledCourseEventStatusLabel = styled(CourseEventStatusLabel)`
  grid-area: status;

  ${({ theme }) =>
    theme.active &&
    css`
      svg {
        color: ${colors.white} !important;
      }
    `}

  ${media.w.min.px768(css`
    align-self: start;
  `)};
`;

const Time = styled.div`
  align-items: center;
  display: flex;
  font-weight: 300;
  grid-area: time;

  ${media.w.min.px768(css`
    align-self: end;
  `)};

  svg {
    margin-right: 0.625rem;
  }
`;

const Actions = styled.div`
  grid-area: actions;

  ${media.w.min.px768(css`
    margin-top: 0.4375rem;
  `)};
`;

export interface CourseEventSelectPanelItemProps {
  courseEvent: CourseEvent;
  dimmed?: boolean;
  selected?: boolean;
  onClick?: () => void;
  actions?: ReactElement;
}

export const CourseEventSelectPanelItem: FC<CourseEventSelectPanelItemProps> = ({
  courseEvent,
  dimmed,
  onClick,
  selected,
  actions,
}) => {
  const endDate = useMemo(
    () => addMinutes(courseEvent.startDateTime, courseEvent.duration ?? 0),
    [courseEvent],
  );

  return (
    <ThemeProvider theme={{ active: selected, dimmed }}>
      <Item onClick={onClick} as={onClick == null ? 'div' : CleanButton} $link={onClick != null}>
        <Date $link={onClick != null}>
          <span>{format(courseEvent.startDateTime, 'dd')}</span>
          {format(courseEvent.startDateTime, 'MMM')}
        </Date>

        <Time>
          <ClockIcon size={12} />
          {format(courseEvent.startDateTime, 'HH:mm')} - {format(endDate, 'HH:mm')}
        </Time>

        <StyledCourseEventStatusLabel courseEvent={courseEvent} />

        {actions != null && <Actions>{actions}</Actions>}
      </Item>
    </ThemeProvider>
  );
};
